/**
 * Marca do LOGIN — qual "roupa" a tela de entrada veste (M5 Group, Regula360
 * ou VORTEK neutra). Cliente da Regula que recebe convite precisa cair numa
 * tela que RECONHECE; VORTEK segue como fallback neutro da plataforma.
 *
 * `resolverMarcaLogin`/`marcaDaEmpresa` são PURAS (testáveis em vitest sem
 * DOM); os wrappers com window/localStorage ficam no fim do arquivo.
 *
 * Ordem de detecção: ?marca= na URL → hostname → localStorage → VORTEK.
 */

import { ehSlugRegula } from "./grupoDaEmpresa";

export type MarcaLogin = "m5" | "regula" | "vortek";

export const ULTIMA_MARCA_KEY = "ultima_marca";

const MARCAS: readonly MarcaLogin[] = ["m5", "regula", "vortek"];

/** Valida um valor cru (query/localStorage). Inválido → null (nunca quebra). */
export function normalizarMarca(v: unknown): MarcaLogin | null {
  if (typeof v !== "string") return null;
  const s = v.trim().toLowerCase();
  return (MARCAS as readonly string[]).includes(s) ? (s as MarcaLogin) : null;
}

/** Núcleo PURO da detecção — recebe os insumos, devolve a marca. */
export function resolverMarcaLogin(input: {
  search?: string | null; // window.location.search (ex.: "?marca=regula")
  hostname?: string | null; // window.location.hostname
  stored?: string | null; // localStorage[ULTIMA_MARCA_KEY]
}): MarcaLogin {
  // 1) ?marca= explícito (links de convite passam a marca do produto)
  try {
    const q = normalizarMarca(new URLSearchParams(input.search ?? "").get("marca"));
    if (q) return q;
  } catch {
    /* search malformado → segue a fila */
  }

  // 2) domínio próprio (futuro regula360.com.br). NÃO mapear "m5" por
  //    hostname: m5group.pages.dev hospeda TODOS os tenants — seria falso
  //    positivo pra cliente Regula/Átomo.
  if ((input.hostname ?? "").toLowerCase().includes("regula")) return "regula";

  // 3) última marca vista (gravada após login bem-sucedido)
  const st = normalizarMarca(input.stored);
  if (st) return st;

  // 4) porta neutra da plataforma
  return "vortek";
}

/**
 * Classifica a marca do PRODUTO a partir da empresa (slug/nome) — mesma
 * convenção do LogoEmpresa.tsx. Átomo e demais white-label ficam na porta
 * neutra VORTEK (o produto deles É a plataforma com o logo trocado).
 *
 * ⚠️ Esta função responde "qual ROUPA a empresa veste", NÃO "de que produto ela
 * é cliente". As duas respostas divergem no Átomo: ele usa o software de obras
 * e mesmo assim veste VORTEK. Pra agrupar/segmentar por produto use
 * `grupoDaEmpresa` (lib/grupoDaEmpresa.ts) — usar esta aqui isola o Átomo.
 */
export function marcaDaEmpresa(
  emp: { slug?: string | null; nome?: string | null; marca?: string | null } | null | undefined
): MarcaLogin {
  // 05/09 (bloqueio 10): a MARCA é dado (`empresas.marca`). Quando vem, manda.
  const dado = normalizarMarca(emp?.marca);
  if (dado) return dado;
  // Fallback: SÓ pelo slug (controlado por nós) — nome é texto livre: uma white-label
  // futura "Regularize Facilities" viraria login Regula360 por substring.
  //
  // A lista de slugs do lado Regula mora em `grupoDaEmpresa.ts`, uma vez só: a
  // pele e o grupo têm que concordar sobre quem é Regula, e concordam por
  // importar a MESMA regra — não por duas cópias iguais que alguém mantém.
  const slug = (emp?.slug ?? "").toLowerCase();
  if (ehSlugRegula(slug)) return "regula";
  if (slug === "m5group") return "m5";
  return "vortek";
}

// ---------- wrappers com side effects (window/localStorage) ----------

export function lerUltimaMarca(): string | null {
  try {
    return localStorage.getItem(ULTIMA_MARCA_KEY);
  } catch {
    return null;
  }
}

export function gravarUltimaMarca(m: MarcaLogin): void {
  try {
    localStorage.setItem(ULTIMA_MARCA_KEY, m);
  } catch {
    /* storage bloqueado (modo privado etc.) — segue sem marca */
  }
}

/**
 * Captura ?marca= válido na ENTRADA do app e persiste. Necessário porque o
 * link de convite aponta pra "/" e o <Navigate to="/login"> descarta a query
 * string — sem persistir aqui, a dica da marca se perderia no redirect.
 * Valor inválido/forjado é simplesmente ignorado (é só skin, não é dado).
 */
export function capturarMarcaDaUrl(): void {
  try {
    const q = normalizarMarca(new URLSearchParams(window.location.search).get("marca"));
    if (q) gravarUltimaMarca(q);
  } catch {
    /* sem window / URL estranha */
  }
}

/** Marca efetiva da tela de login (query > hostname > localStorage > VORTEK). */
export function detectarMarcaLogin(): MarcaLogin {
  try {
    return resolverMarcaLogin({
      search: window.location.search,
      hostname: window.location.hostname,
      stored: lerUltimaMarca(),
    });
  } catch {
    return "vortek";
  }
}
